import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getAttendanceStats } from '../utils/attendanceApi';
import { AttendanceStats } from '../types/attendance';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import WorkCertificate from '../components/attendance/WorkCertificate';

const ProfilePage: React.FC = () => {
  const { user, logout } = useAuth();
  const [stats, setStats] = useState<AttendanceStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const response = await getAttendanceStats();
      setStats(response.data);
    } catch (error) {
      console.error('통계 로드 실패:', error);
      toast.error('통계를 불러올 수 없습니다');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    if (window.confirm('정말 로그아웃 하시겠습니까?')) {
      logout();
      toast.success('로그아웃 되었습니다');
    }
  };

  if (loading) {
    return (
      <main className="container mx-auto px-4 py-6 pb-0">
        <div className="max-w-2xl mx-auto">
          <div className="text-center py-12">
            <div className="text-4xl mb-4">⏳</div>
            <p className="text-gray-600">로딩 중...</p>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="container mx-auto px-4 py-6 pb-0">
      <div className="max-w-2xl mx-auto space-y-4">
        {/* 헤더 */}
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-primary mb-2">
            👤 내 정보
          </h1>
          <p className="text-sm text-gray-600">
            나의 근무 현황과 정보를 확인하세요
          </p>
        </div>

        {/* 1. 개인 정보 카드 */}
        <div className="card-azocsii">
          <div className="flex items-center space-x-4 mb-4">
            <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-3xl">
              👤
            </div>
            <div>
              <h2 className="text-xl font-bold text-primary">{user?.name}님</h2>
              <p className="text-sm text-gray-600">{user?.email}</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-base-cream rounded-lg p-3">
              <p className="text-xs text-gray-600 mb-1">역할</p>
              <p className="font-bold text-primary">
                {user?.role === 'COMPANY_ADMIN' 
                  ? '👑 회사 관리자' 
                  : user?.role === 'TEAM_LEADER' 
                  ? '⭐ 팀장' 
                  : '일반 사용자'}
              </p>
            </div>
            <div className="bg-base-cream rounded-lg p-3">
              <p className="text-xs text-gray-600 mb-1">가입일</p>
              <p className="font-bold text-primary text-sm">
                {user?.createdAt ? format(new Date(user.createdAt), 'yyyy.MM.dd') : '-'}
              </p>
            </div>
          </div>

          {/* 회사/팀 정보 */}
          <div className="mt-3 grid grid-cols-2 gap-3">
            <div className="bg-purple-50 rounded-lg p-3 border border-purple-200">
              <p className="text-xs text-purple-600 mb-1">🏢 회사</p>
              <p className="font-bold text-purple-800 text-sm truncate">
                {user?.company?.name || '-'}
              </p>
            </div>
            <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
              <p className="text-xs text-blue-600 mb-1">👥 팀</p>
              <p className="font-bold text-blue-800 text-sm truncate">
                {user?.role === 'COMPANY_ADMIN' 
                  ? '관리자' 
                  : user?.team?.name || '-'}
              </p>
            </div>
          </div>
        </div>

        {/* 2. 이번 주 근무 요약 */}
        <div className="card-azocsii">
          <h3 className="text-lg font-bold text-primary mb-4 flex items-center">
            <span className="mr-2">📊</span>
            이번 주 근무 현황
          </h3>
          <div className="space-y-3">
            {/* 총 근무시간 */}
            <div className="flex items-center justify-between p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border-2 border-blue-200">
              <div className="flex items-center space-x-3">
                <span className="text-2xl">⏰</span>
                <span className="font-medium text-gray-700">총 근무시간</span>
              </div>
              <span className="text-2xl font-black text-indigo-600">
                {stats?.weeklyHours.toFixed(1) || 0}h
              </span>
            </div>

            {/* 연속 근무일 */}
            <div className="flex items-center justify-between p-3 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border-2 border-green-200">
              <div className="flex items-center space-x-3">
                <span className="text-2xl">📅</span>
                <span className="font-medium text-gray-700">연속 근무</span>
              </div>
              <span className="text-2xl font-black text-green-600">
                {stats?.consecutiveDays || 0}일
              </span>
            </div>

            {/* 지각/조퇴 */}
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 bg-orange-50 rounded-lg border border-orange-200">
                <p className="text-xs text-gray-600 mb-1">⚠️ 지각</p>
                <p className="text-xl font-black text-orange-600">
                  {stats?.late || 0}회
                </p>
              </div>
              <div className="p-3 bg-orange-50 rounded-lg border border-orange-200">
                <p className="text-xs text-gray-600 mb-1">🏃 조퇴</p>
                <p className="text-xl font-black text-orange-600">
                  {stats?.earlyLeave || 0}회
                </p>
              </div>
            </div>

            {/* 만근수당 판정 */}
            <div className={`p-3 rounded-lg border-2 ${
              (stats?.late || 0) === 0 && (stats?.earlyLeave || 0) === 0 && (stats?.absent || 0) === 0
                ? 'bg-gradient-to-r from-green-100 to-emerald-100 border-green-400'
                : 'bg-gray-100 border-gray-300'
            }`}>
              <div className="flex items-center justify-between">
                <span className="font-bold text-gray-700">✅ 만근수당</span>
                <span className={`text-lg font-black ${
                  (stats?.late || 0) === 0 && (stats?.earlyLeave || 0) === 0 && (stats?.absent || 0) === 0
                    ? 'text-green-600'
                    : 'text-gray-500'
                }`}>
                  {(stats?.late || 0) === 0 && (stats?.earlyLeave || 0) === 0 && (stats?.absent || 0) === 0
                    ? '가능! 🎉'
                    : '불가'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* 3. 페어니스 포인트 */}
        <div className="card-azocsii bg-gradient-to-br from-purple-50 to-pink-50 border-2 border-purple-200">
          <h3 className="text-lg font-bold text-primary mb-3 flex items-center">
            <span className="mr-2">🎯</span>
            페어니스 포인트
          </h3>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">현재 보유 포인트</p>
              <p className="text-3xl font-black text-purple-600">
                {user?.fairnessPoints || 0} P
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-600 mb-1">로또 당첨률 보너스</p>
              <p className="text-2xl font-black text-pink-600">
                +{((user?.fairnessPoints || 0) / 10).toFixed(0)}%
              </p>
            </div>
          </div>
          <div className="mt-3 p-3 bg-white/60 rounded-lg">
            <div className="text-xs text-gray-600 leading-tight">
              <div>💡 로또 탈락 시마다 10P 적립!</div>
              <div className="text-right">포인트가 높을수록 다음 추첨 확률 UP!</div>
            </div>
          </div>
        </div>

        {/* 4. 빠른 액션 */}
        <div className="card-azocsii space-y-3">
          <h3 className="text-lg font-bold text-primary mb-3">
            ⚡ 빠른 액션
          </h3>
          
          {/* 근무 증빙 다운로드 */}
          <WorkCertificate />

          {/* 비밀번호 변경 */}
          <button
            onClick={() => toast('준비 중입니다', { icon: '🔧' })}
            className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-blue-100 via-cyan-100 to-teal-100 hover:from-blue-200 hover:to-teal-200 rounded-xl transition-all border-2 border-blue-300 hover:border-blue-400 hover:scale-105 active:scale-95"
          >
            <div className="flex items-center space-x-3">
              <span className="text-2xl">🔒</span>
              <span className="font-medium text-gray-700">비밀번호 변경</span>
            </div>
            <span className="text-gray-400">→</span>
          </button>

          {/* 로그아웃 */}
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-gray-200 via-slate-200 to-zinc-200 hover:from-gray-300 hover:to-zinc-300 rounded-xl transition-all border-2 border-gray-400 hover:border-gray-500 hover:scale-105 active:scale-95"
          >
            <div className="flex items-center space-x-3">
              <span className="text-2xl">🚪</span>
              <span className="font-medium text-gray-700">로그아웃</span>
            </div>
            <span className="text-gray-400">→</span>
          </button>
        </div>
      </div>
    </main>
  );
};

export default ProfilePage;
